/**
 * Pure form logic for the Agent Teams settings page. Kept free of React so the
 * validation rules and the change-only patch shape are unit-testable.
 *
 * The bounds come from the wire schema so the form cannot accept a value the
 * Gateway rejects; the duplicate-name check is a client-side courtesy over the
 * names already loaded, not the authority — the Gateway re-checks
 * case-insensitively against every team.
 */

// Subpath import, not the package root: this module is bundled into the
// browser SPA, and the root index pulls in node:crypto-dependent modules that
// cannot resolve there.
import { AGENT_TEAM_DESCRIPTION_MAX, AGENT_TEAM_NAME_MAX } from "../contracts/teams.js";

export { AGENT_TEAM_DESCRIPTION_MAX, AGENT_TEAM_NAME_MAX };

export interface AgentTeamFormValues {
  name: string;
  description: string;
}

export type AgentTeamFormErrors = Partial<Record<keyof AgentTeamFormValues, string>>;

export function emptyAgentTeamFormValues(): AgentTeamFormValues {
  return { name: "", description: "" };
}

export function agentTeamToFormValues(team: {
  name: string;
  description?: string;
}): AgentTeamFormValues {
  return { name: team.name, description: team.description ?? "" };
}

export function validateAgentTeamForm(
  values: AgentTeamFormValues,
  options: { otherNames?: readonly string[] } = {},
): { valid: boolean; errors: AgentTeamFormErrors } {
  const errors: AgentTeamFormErrors = {};
  const name = values.name.trim();
  if (!name) errors.name = "Team name is required";
  else if (name.length > AGENT_TEAM_NAME_MAX) {
    errors.name = `Team name must be ${AGENT_TEAM_NAME_MAX} characters or fewer`;
  } else if (
    // Same locale-independent fold the Gateway's authoritative check uses, so a
    // browser in a Turkish locale cannot disagree with the server about whether
    // two names collide.
    (options.otherNames ?? []).some((other) => other.toLowerCase() === name.toLowerCase())
  ) {
    errors.name = `A team named "${name}" already exists`;
  }
  if (values.description.trim().length > AGENT_TEAM_DESCRIPTION_MAX) {
    errors.description = `Description must be ${AGENT_TEAM_DESCRIPTION_MAX} characters or fewer`;
  }
  return { valid: Object.keys(errors).length === 0, errors };
}

export function agentTeamFormToCreatePayload(values: AgentTeamFormValues): {
  name: string;
  description?: string;
} {
  const description = values.description.trim();
  return { name: values.name.trim(), ...(description ? { description } : {}) };
}

/**
 * Only the fields that actually changed. The wire rejects an empty patch, and
 * sending an unchanged name would make every save contend with the uniqueness
 * check against the team's own row.
 */
export function agentTeamFormToUpdatePayload(
  values: AgentTeamFormValues,
  baseline: AgentTeamFormValues,
): { name?: string; description?: string } {
  const patch: { name?: string; description?: string } = {};
  const name = values.name.trim();
  const description = values.description.trim();
  if (name !== baseline.name.trim()) patch.name = name;
  // `""` is meaningful here: it is how the patch clears an existing
  // description, which omitting the key cannot express.
  if (description !== baseline.description.trim()) patch.description = description;
  return patch;
}
